/* eslint-disable max-lines-per-function */
import { ChangeEventHandler, forwardRef, useEffect, useState } from 'react';

import { updateBoxState, defaultFunction } from './checkbox-common';
import { Checkbox, CheckboxProps, ChevronDownIcon, RadioButton, RadioButtonProperties } from '../..';

type AccordionCheckboxProps = Omit<CheckboxProps, 'hideLabel'>;
export interface RadioAccordionProps extends RadioButtonProperties {
  checkboxData: AccordionCheckboxProps[];
  defaultOpen: boolean;
  onCheckboxChange: (radioValue: string, selectedBoxes: string[]) => void;
}

export const RadioAccordion = forwardRef<HTMLInputElement, RadioAccordionProps>((props, ref) => {
  const { checkboxData, defaultOpen, onChange = defaultFunction, ...radioProps } = props;
  const [open, setOpen] = useState(defaultOpen);
  const [boxes, setBoxes] = useState(checkboxData);

  const onRadioChange: ChangeEventHandler<HTMLInputElement> = (e) => {
    setBoxes(updateBoxState(e.target.checked));
    onChange(e);
    radioProps.onCheckboxChange(
      radioProps.value,
      boxes.map((box) => box.value)
    );
  };

  const onCheckboxChange: ChangeEventHandler<HTMLInputElement> = (e) => {
    setBoxes((curr) => {
      return curr.map((box) => {
        if (e.target.value === box.value) {
          return { ...box, isChecked: e.target.checked };
        }
        return box;
      });
    });

    radioProps.onCheckboxChange(
      radioProps.value,
      boxes
        .filter((box) => {
          return box.isChecked ? e.target.value !== box.value : e.target.value === box.value;
        })
        .map((box) => box.value)
    );
  };

  useEffect(() => {
    if (radioProps.checked) {
      // if checked value comes from props, check all
      // unless a checkbox is checked
      if (!boxes.some((b) => b.isChecked)) {
        setBoxes(updateBoxState(true));
      }
    } else {
      setBoxes(updateBoxState(false));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [radioProps.checked]);

  return (
    <div className="flex flex-col">
      <div className="flex cursor-pointer flex-row">
        <div className="flex-1">
          <RadioButton {...radioProps} onChange={onRadioChange} ref={ref} />
        </div>
        <ChevronDownIcon
          onClick={() => {
            setOpen((curr) => !curr);
          }}
          className={`max-h-6 max-w-6 transition-all ${open ? 'rotate-180	' : 'rotate-0'}`}
        />
      </div>
      {open && (
        <div className="ml-10 mt-4 space-y-4">
          {boxes.map((props, index) => {
            return <Checkbox key={index} {...props} hideLabel={false} onChange={onCheckboxChange} />;
          })}
        </div>
      )}
    </div>
  );
});

RadioAccordion.displayName = 'RadioAccordion';

export default RadioAccordion;
